<template>
  <div class="home">
    <div>
      <section>
        <div class="py-8 px-8">
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="col-12 col-lg-auto mb-4 mb-lg-0">
              <div class="d-flex align-items-center">
                <h4 class="mb-0 me-2">{{ $t('plans') }}</h4>
              </div>
            </div>
            <div class="col-12 col-lg-auto d-flex align-items-center">
              <div class="input-group flex-grow-1" style="border-bottom: 1px solid #ddd;">
                                <input class="form-control border-0 search-input" autocomplete="off" type="text" v-model="search" :placeholder="$t('search')">
                            </div>
            </div>
          </div>
        </div>
      </section>

      <section class="">
        <div class="px-8">
          <div class="row align-items-end g-3 mb-4">
            <div class="col"></div>
            <div class="col-md-2">
              <button @click="create" class="btn-sm btn btn-success w-100">
                <span class="d-inline-block me-2 .text-primary-dark">
                  <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                </span>
                <span>{{ $t('plan') }}</span>
              </button>
            </div>
          </div>

          <div class="page-table-container mb-6 bg-white rounded shadow">
            <!-- table -->
            <div class="table-responsive">
              <table class="table mb-0 table-borderless table-striped small">
                <thead>
                  <tr class="text-secondary">
                    <sortable-table-header field="id" label="id" :sort-field="sortField" :sort-direction="sortDirection" @sort="toggleSort" />
                    <sortable-table-header field="name" label="name" :sort-field="sortField" :sort-direction="sortDirection" @sort="toggleSort" />
                    <sortable-table-header field="description" label="description" :sort-field="sortField" :sort-direction="sortDirection" @sort="toggleSort" />
                    <sortable-table-header field="price" label="price" :sort-field="sortField" :sort-direction="sortDirection" @sort="toggleSort" />
                    <sortable-table-header field="duration" label="plan_duration" :sort-field="sortField" :sort-direction="sortDirection" @sort="toggleSort" />
                    <th class="pt-4 pb-3 px-6">{{ $t('plan_features') }}</th>
                    <th class="pt-4 pb-3 px-6">{{ $t('actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="plan in sortedPlans" :key="plan.id">
                    <td class="py-5 px-6">{{ plan.id }}</td>
                    <td class="py-5 px-6">{{ plan.name }}</td>
                    <td class="py-5 px-6">{{ plan.description }}</td>
                    <td class="py-5 px-6">
                      <div>{{ formatPrice(plan.price) }}</div>
                      <small class="text-muted d-block" style="font-size: 10px;">{{ $t('extra_user', { price: formatPrice(9.99).replace(' ', '') }) }}</small>
                    </td>
                    <td class="py-5 px-6">{{ plan.duration }} {{ $t('month') }}</td>
                    <td class="py-5 px-6">
                      <ul class="list-unstyled mb-0">
                        <li v-for="(feature, index) in plan.features" :key="index">
                          {{ feature }}
                        </li>
                      </ul>
                    </td>
                    <td class="py-5 px-6">
                      <button @click="editRow(plan)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
                        {{ $t('edit') }}
                      </button>
                      <button @click="deleteRow(plan)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" />
                        {{ $t('delete') }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- table -->
            <table-loading-bar v-if="loading" class="page-table-state text-center" />
            <div v-if="loading === false && !plans.length" class="page-table-state text-center" style="padding:1rem">{{ $t('no_plans_found') }}</div>
          </div>
        </div>
      </section>
    </div>

    <!-- Create/Edit Plan Modal -->
    <div class="modal fade" :class="{ show: showCreateModal }" tabindex="-1" role="dialog" :style="{ display: showCreateModal ? 'block' : 'none' }">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingPlan ? $t('edit_plan') : $t('create_plan') }}</h5>
            <button type="button" class="btn-close" @click="showCreateModal = false"></button>
          </div>
          <div class="modal-body" style="text-align:left">
            <form @submit.prevent="saveRow" class="plan-form">
              <div class="row">
                <div class="col-sm-6 mb-3">
                  <label class="form-label">{{ $t('plan_name') }}</label>
                  <input v-model="plan.name" class="form-control" required />
                </div>
                <div class="col-sm-6 mb-3">
                  <label class="form-label">{{ $t('plan_price') }} {{business.currency}}</label>
                  <input type="number" v-model="plan.price" class="form-control" required min="0" step="0.01" />
                </div>
                <div class="col-sm-6 mb-3">
                  <label class="form-label">{{ $t('plan_duration') }}</label>
                  <input type="number" v-model="plan.duration" class="form-control" required min="1" />
                </div>
                <div class="col-sm-12 mb-3">
                  <label class="form-label">{{ $t('description') }}</label>
                  <textarea v-model="plan.description" class="form-control" required></textarea>
                </div>
                <div class="col-sm-12 mb-3">
                  <label class="form-label">{{ $t('plan_features') }}</label>
                  <div v-for="(feature, index) in plan.features" :key="index" class="feature-input mb-2">
                    <div class="input-group">
                      <input v-model="plan.features[index]" class="form-control" />
                      <button type="button" @click="removeFeature(index)" class="btn btn-danger">
                        <AppIcon name="bi-trash" class="bi bi-trash" />
                      </button>
                    </div>
                  </div>
                  <button type="button" @click="addFeature" class="btn btn-secondary">
                    <AppIcon name="bi-plus" class="bi bi-plus" /> {{ $t('add_feature') }}
                  </button>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showCreateModal = false">{{ $t('close') }}</button>
            <button type="button" class="btn btn-primary" @click="saveRow" :disabled="loading">
              {{ editingPlan ? $t('save') : $t('create') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showCreateModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import helper from '../helper'
import sortableList from '../mixins/sortableList'

export default {
  name: 'Plans',
  persistedFilterState: { key: 'plans', fields: ['search', 'sortField', 'sortDirection'] },
  mixins: [sortableList],
  data() {
    return {
      plans: [],
      showCreateModal: false,
      editingPlan: null,
      plan: {
        name: '',
        description: '',
        price: 0,
        duration: 1,
        features: ['']
      },
      search : '',
      loading: false
    }
  },
  mounted() {
    window.mixpanel.track("plans");
    //check permissions
    if(helper.checkAccess('view_plans') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_plans') == -2) return this.$router.push({path : '/subscribe'})

    this.getPlans()
  },
  computed: {
    sortedPlans() {
      return this.sortRows(this.plans, this.sortField, this.sortDirection)
    },
    business(){ return this.$store.getters.BUSINESS},
    formatPrice() {
      return (price) => {
        let convertedPrice;
        let currencySymbol;

        switch (this.business.currency) {
          case 'dh':
            convertedPrice = price * 10;
            currencySymbol = 'DH';
            break;
          default:
            convertedPrice = price;
            currencySymbol = '$';
            break;
        }

        return `${convertedPrice} ${currencySymbol}`;
      }
    }
  },
  methods: {
    async getPlans() {
      try {
        this.loading = true
        const response = await helper.customGet(`${process.env.VUE_APP_API}/plans`)
        this.plans = response.data.data
        this.loading = false
      } catch (error) {
        this.loading = false
        window.vueOpener.open({
          message: this.$t('failed_to_fetch_plans'),
          type: 'error',
          position: 'bottom'
        });
      }
    },
    create() {
      this.clearForm()
      this.showCreateModal = true
    },
    editRow(row) {
      this.plan = { ...row }
      this.editingPlan = row
      this.showCreateModal = true
    },
    async saveRow() {
      try {
        this.loading = true
        if (this.editingPlan) {
          await helper.customPatch(`${process.env.VUE_APP_API}/plans/${this.editingPlan.id}`, this.plan)
        } else {
          await helper.customPost(`${process.env.VUE_APP_API}/plans`, this.plan)
        }
        this.loading = false
        this.clearForm()
        this.getPlans()
        this.showCreateModal = false
      } catch (error) {
        this.loading = false
        window.vueOpener.open({
          message: this.$t('failed_to_save_plan'),
          type: 'error',
          position: 'bottom'
        });
      }
    },
    async deleteRow(row) {
      const result = await this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
      })
      if (result.isConfirmed) {
        try {
          await helper.customDelete(`${process.env.VUE_APP_API}/plans/${row.id}`)
          this.plans = helper.withoutDeletedRecords(this.plans, row.id)
        } catch (error) {
          window.vueOpener.open({
            message: this.$t('failed_to_delete_plan'),
            type: 'error',
            position: 'bottom'
          });
        }
      }
    },
    addFeature() {
      this.plan.features.push('')
    },
    removeFeature(index) {
      this.plan.features.splice(index, 1)
    },
    clearForm() {
      this.plan = {
        name: '',
        description: '',
        price: 0,
        duration: 1,
        features: ['']
      }
      this.editingPlan = null
    }
  }
}
</script>

<style scoped>
.table-borderless>:not(caption)>*>* {
  padding: 5px !important;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

.modal {
  z-index: 1050;
}

.feature-input .input-group {
  margin-bottom: 0.5rem;
}

.btn-xs {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1.5;
  border-radius: 0.2rem;
}
</style>
